/**
 * The strategic save bridge (plan I2; spec §14, D-099): every M2 slot write carries the Reach's strategic save in
 * the storage's companion store (`strategic`), written in the same IndexedDB transaction as the slot text. The
 * slot text is never touched, so M2's record and its 50 KB cap stay as they are.
 *
 * - **Keyed by record, not slot** (D-TBD: strategic saves keyed by record). M2's autosave moves the raw texts down
 *   one slot each time, so a slot name would point at the wrong save. The key is
 *   `${career.seed}:${career.gameTime}:${savedAt}` read from the record JSON (M7 S41: with `savedAt`, since docked
 *   saves share a game time; loads fall back to the legacy key without it); after each write, entries that no slot's record points to any more are deleted.
 * - **New save or moved autosave.** A slot text whose record was written less than `FRESH_RECORD_S` ago (its
 *   `savedAt`) is a new save: the bridge awaits `client.save()` (the worker serializes) and writes it with the
 *   slot. An older text is an autosave moving down a slot (or an imported file): the slot alone is written. An
 *   imported file brings its own companion (`strategicCompanion`), which import stores (`save/exportImport.ts`, D-337).
 * - Each entry is an envelope `{ savedAt: epochMs, save }`; the load path (`service.ts`) catches up the time since
 *   `savedAt`.
 */
import { logger } from '../core/logger'
import type { CompanionStore, SaveCompanion, SaveStorage } from '../save/storage'
import { encodeEnvelope, envelopeSchema, recordKeyOf, type StrategicEnvelope } from '../save/strategicCompanion'
import type { StrategicSave } from '../strategic/types'
import type { SaveSlotId } from '../ui/contracts'

// The key and envelope live with the save code (export/import bundles them; D-337); re-exported for the link.
export { companionKeys, encodeEnvelope, recordKeyOf, strategicKey, type StrategicEnvelope } from '../save/strategicCompanion'

/**
 * A slot text whose record is younger than this (seconds, by its `savedAt`) is a new save. Not tuning: it only has
 * to be longer than one save takes and shorter than the autosave interval.
 */
export const FRESH_RECORD_S = 10

/** What the bridge needs from the strategic client. */
export interface SaveBridgeClient {
  save(): Promise<StrategicSave>
}

/** A stored envelope, or null when it is missing or unreadable (the caller starts a fresh Reach). */
export function decodeEnvelope(text: string | null): StrategicEnvelope | null {
  if (text === null) return null
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    logger.warn('strategic save: the stored Reach is not JSON; starting a fresh Reach')
    return null
  }
  const r = envelopeSchema.safeParse(raw)
  if (!r.success) {
    logger.warn('strategic save: the stored Reach is malformed; starting a fresh Reach', r.error.issues)
    return null
  }
  return r.data
}

/** The strategic save stored under the first of `keys` that has one (a record's key, then its legacy key), or null. */
export async function loadStrategicSave(store: CompanionStore | undefined, ...keys: string[]): Promise<StrategicEnvelope | null> {
  if (store === undefined) return null
  try {
    for (const key of keys) {
      const text = await store.get(key)
      if (text !== null) return decodeEnvelope(text)
    }
    return null
  } catch (err) {
    logger.warn('strategic save: reading the stored Reach failed; starting a fresh Reach', err)
    return null
  }
}

export interface SaveBridgeOptions {
  /** The strategic client (null while it is not initialized: slot writes then go alone). */
  client: () => SaveBridgeClient | null
  /** Wall clock in ms (default `Date.now`). */
  now?: () => number
}

/** The companion to register with `setSaveCompanion`. */
export function createSaveBridge(opts: SaveBridgeOptions): SaveCompanion {
  const now = opts.now ?? Date.now
  return {
    async beforePut(_slot: SaveSlotId, text: string): Promise<{ key: string; value: string } | null> {
      const client = opts.client()
      if (client === null) return null
      const rec = recordKeyOf(text)
      if (rec === null) return null
      const t = now()
      if (t - rec.savedAtMs >= FRESH_RECORD_S * 1000) return null
      const save = await client.save()
      return { key: rec.key, value: encodeEnvelope({ savedAt: t, save }) }
    },

    async afterPut(storage: SaveStorage): Promise<void> {
      const store = storage.companion
      if (store === undefined) return
      const live = new Set<string>()
      for (const slot of await storage.list()) {
        const text = await storage.get(slot)
        const rec = text === null ? null : recordKeyOf(text)
        if (rec === null) continue
        live.add(rec.key)
        live.add(rec.legacyKey) // a save stored before S41 keeps its Reach
      }
      for (const key of await store.keys()) if (!live.has(key)) await store.delete(key)
    },
  }
}
